import { GroupedRow } from 'modules/pond/api/types'
import { useMemo } from 'react'

import { ShrimpSizeRow } from '../constants'

export type CellValue = GroupedRow['values'][number] | null | undefined

export interface PondBlockRow {
  label: string
  values: CellValue[]
}

export interface PondBlockData {
  pond: string
  dates: string[]
  rows: PondBlockRow[]
}

const hasValue = (value: CellValue) => value !== null && value !== undefined && value !== ''

// NK-281: regroups the farm-wide matrix into one block per pond, and keeps for each
// pond only the dates it was actually measured on — the backend's date axis is the
// union across ponds, so without this a pond's block is mostly columns of dashes
// from the days some other pond was measured.
//
// `columns` and each row's `values` arrive oldest → newest (the selector reverses
// the backend's newest-first pages), which is the order the blocks draw them in.
export const usePondBlocks = (columns: string[] | undefined, rows: GroupedRow[] | undefined) =>
  useMemo<PondBlockData[]>(() => {
    if (!columns?.length || !rows?.length) return []

    const byPond = new Map<string, GroupedRow[]>()
    for (const row of rows) {
      const pondRows = byPond.get(row.group)
      if (pondRows) pondRows.push(row)
      else byPond.set(row.group, [row])
    }

    return Array.from(byPond, ([pond, pondRows]) => {
      const sizeRow = pondRows.find((row) => row.label === ShrimpSizeRow.Size)
      const kept = columns
        .map((_, idx) => idx)
        .filter((idx) => hasValue(sizeRow?.values[idx] as CellValue))

      return {
        pond,
        dates: kept.map((idx) => columns[idx]),
        rows: pondRows.map((row) => ({
          label: row.label,
          values: kept.map((idx) => row.values[idx] as CellValue),
        })),
      }
    })
  }, [columns, rows])
